import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/api";
import { X } from "lucide-react";

const PRICE_MAX = 20000;
const STATES = ["vivant", "abattu", "pieces", "pret-a-cuire"];

export const Filters = ({ categories, regions, filters, onChange, onReset }) => {
  const brand = useBrand();
  const { t } = useT();

  const toggleCategory = (slug) =>
    onChange({ ...filters, category: filters.category === slug ? "" : slug });

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-lg text-secondary">{t("catalog.filters")}</h3>
        <Button variant="ghost" size="sm" onClick={onReset} data-testid="reset-filters-btn"
          className="text-muted-foreground hover:text-primary h-8 px-2">
          <X className="h-3.5 w-3.5 mr-1" /> {t("common.reset")}
        </Button>
      </div>

      <div>
        <Label className="text-sm font-semibold text-secondary">{t("catalog.category")}</Label>
        <div className="mt-3 space-y-2.5">
          {categories.map((cat) => (
            <label key={cat.slug} className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid={`filter-category-${cat.slug}`}>
              <Checkbox checked={filters.category === cat.slug} onCheckedChange={() => toggleCategory(cat.slug)} />
              <span className="text-foreground/80">{t(`categories.${cat.slug}`)}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-secondary">{t("catalog.state")}</Label>
        <Select value={filters.state || "all"} onValueChange={(v) => onChange({ ...filters, state: v === "all" ? "" : v })}>
          <SelectTrigger className="mt-3" data-testid="filter-state-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("catalog.allStates")}</SelectItem>
            {STATES.map((s) => <SelectItem key={s} value={s}>{t(`states.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-semibold text-secondary">{t("catalog.region")}</Label>
        <Select value={filters.region || "all"} onValueChange={(v) => onChange({ ...filters, region: v === "all" ? "" : v })}>
          <SelectTrigger className="mt-3" data-testid="filter-region-trigger">
            <SelectValue placeholder={t("catalog.allRegions")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("catalog.allRegions")}</SelectItem>
            {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-semibold text-secondary">{t("catalog.location")}</Label>
        <Input value={filters.location || ""} onChange={(e) => onChange({ ...filters, location: e.target.value })}
          placeholder={t("catalog.locationPlaceholder")} className="mt-3" data-testid="filter-location-input" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-secondary">{t("catalog.maxPrice")}</Label>
          <span className="text-sm text-primary font-semibold">
            {formatPrice(filters.maxPrice ?? PRICE_MAX, brand.currencySymbol)}
          </span>
        </div>
        <Slider data-testid="filter-price-slider" className="mt-4" min={500} max={PRICE_MAX} step={500}
          value={[filters.maxPrice ?? PRICE_MAX]} onValueChange={([v]) => onChange({ ...filters, maxPrice: v })} />
      </div>

      <label className="flex items-center justify-between cursor-pointer" data-testid="filter-delivery-toggle">
        <span className="text-sm font-semibold text-secondary">{t("catalog.deliveryOnly")}</span>
        <Switch checked={!!filters.delivery} onCheckedChange={(v) => onChange({ ...filters, delivery: v })} />
      </label>
    </div>
  );
};
